import { Settings, LogOut } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

interface UserFooterProps {
  collapsed: boolean
}

export function UserFooter({ collapsed }: UserFooterProps) {
  const navigate = useNavigate()
  const { user, signOut } = useAuth()

  const userEmail = user?.email || 'Unknown'
  const userInitials = userEmail.split('@')[0].slice(0, 2).toUpperCase()
  const userName = userEmail.split('@')[0]

  const handleSignOut = async () => {
    await signOut()
    navigate('/login', { replace: true })
  }

  const avatar = (
    <Avatar className="h-7 w-7 shrink-0">
      <AvatarFallback className="text-xs font-medium text-white" style={{ background: 'linear-gradient(to bottom right, #1b449d, #132f6e)' }}>
        {userInitials}
      </AvatarFallback>
    </Avatar>
  )

  return (
    <div className="border-t border-sidebar-border px-3 py-2.5 shrink-0">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              'flex w-full items-center gap-2 rounded-md p-1 text-left transition-colors hover:bg-sidebar-accent',
              collapsed && 'justify-center'
            )}
          >
            {collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>{avatar}</TooltipTrigger>
                <TooltipContent side="right" align="center">
                  <div className="flex flex-col">
                    <span className="font-medium">{userName}</span>
                    <span className="text-xs text-muted-foreground">Admin</span>
                  </div>
                </TooltipContent>
              </Tooltip>
            ) : (
              <>
                {avatar}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-muted-foreground truncate">
                    {userName}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Admin
                  </div>
                </div>
              </>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side={collapsed ? 'right' : 'top'}
          align="start"
          className="w-48"
          sideOffset={8}
        >
          <DropdownMenuItem onClick={() => navigate('/settings')}>
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleSignOut}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
